import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { translateApiError } from "../i18n/apiError";
import { Notification } from "../types/api";
import { apiService } from "../services/api";
import { htmlToText } from "../utils/notificationHtml";

interface NotificationItemProps {
  notification: Notification;
  onRead: (id: number) => void;
  onDismiss: (id: number) => void;
  onClick?: (notification: Notification) => void;
  compact?: boolean;
}

export const NotificationItem: React.FC<NotificationItemProps> = ({
  notification,
  onRead,
  onDismiss,
  onClick,
  compact = false,
}) => {
  const { t } = useTranslation("notificationItem");

  const formatTimeAgo = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);

    if (seconds < 60) return t("timeAgo.justNow");
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return t("timeAgo.minutes", { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t("timeAgo.hours", { count: hours });
    const days = Math.floor(hours / 24);
    if (days < 30) return t("timeAgo.days", { count: days });
    const months = Math.floor(days / 30);
    return t("timeAgo.months", { count: months });
  };

  const getSeverityColor = (severity: string, isUnread: boolean): string => {
    // Read notifications are always gray/subdued
    if (!isUnread) {
      return "bg-gray-50 border-gray-200 hover:bg-gray-100";
    }

    // Unread notifications get severity colors
    switch (severity) {
      case "critical":
        return "bg-red-100 border-red-300 hover:bg-red-200";
      case "high":
        return "bg-yellow-100 border-yellow-300 hover:bg-yellow-200";
      case "medium":
        return "bg-blue-100 border-blue-300 hover:bg-blue-200";
      case "low":
      default:
        return "bg-gray-100 border-gray-300 hover:bg-gray-200";
    }
  };

  const getSeverityDot = (severity: string): string => {
    switch (severity) {
      case "critical":
        return "bg-red-500";
      case "high":
        return "bg-yellow-500";
      case "medium":
        return "bg-blue-500";
      case "low":
      default:
        return "bg-gray-500";
    }
  };

  const handleClick = async () => {
    // Mark as read first (optimistic update)
    if (!notification.read_at) {
      onRead(notification.id);
    }

    // Then handle navigation/callback
    if (onClick) {
      onClick(notification);
    }
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    onDismiss(notification.id);
  };

  const [revoking, setRevoking] = useState(false);
  const [revokeMessage, setRevokeMessage] = useState<string | null>(null);
  const piUserId = (notification.metadata as { pi_user_id?: number } | undefined)?.pi_user_id;

  const handleRevokePI = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!piUserId) return;
    try {
      setRevoking(true);
      await apiService.adminRevokePI(piUserId);
      setRevokeMessage(t("revokePI.success"));
      onDismiss(notification.id);
    } catch (err) {
      setRevokeMessage(translateApiError(t, err));
    } finally {
      setRevoking(false);
    }
  };

  const isUnread = !notification.read_at;

  return (
    <div
      className={`border rounded-lg p-3 transition-all cursor-pointer ${
        getSeverityColor(notification.severity, isUnread)
      } ${isUnread ? "border-l-4" : ""}`}
      onClick={handleClick}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          {/* Only show colored severity dot for unread notifications */}
          {isUnread && (
            <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${getSeverityDot(notification.severity)}`} />
          )}
          {/* For read notifications, show empty space or no dot */}
          {!isUnread && (
            <div className="w-2 h-2 mt-1.5 flex-shrink-0" />
          )}
          <div className="flex-1 min-w-0">
            <h4 className={`text-sm ${isUnread ? "font-bold" : "font-medium"} ${isUnread ? "text-gray-900" : "text-gray-600"} truncate`}>
              {notification.title}
            </h4>
            <p className={`text-sm ${isUnread ? "text-gray-700" : "text-gray-500"} mt-1 ${compact ? "line-clamp-2" : ""}`}>
              {notification.content_format === "html"
                ? htmlToText(notification.message)
                : notification.message}
            </p>
            {notification.type === "pi_no_active_projects" && piUserId && (
              <div className="mt-2 flex items-center gap-2">
                <button
                  onClick={handleRevokePI}
                  disabled={revoking}
                  className="text-xs px-2 py-1 rounded border border-red-300 bg-white text-red-600 hover:bg-red-50 disabled:opacity-50"
                >
                  {revoking ? t("revokePI.revoking") : t("revokePI.button")}
                </button>
                {revokeMessage && (
                  <span className="text-xs text-gray-600">{revokeMessage}</span>
                )}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-1">
              {formatTimeAgo(notification.created_at)}
            </p>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-gray-400 hover:text-gray-600 flex-shrink-0 p-1"
          title={t("dismissTitle")}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
    </div>
  );
};
